<script lang="ts" setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, inject, Ref, useTemplateRef } from 'vue'
import { getLatestMarkerPlacement, type MarkerPlacementSnapshot } from '@/components/player/overlays/marker-canvas/markerPlacement'
import { EventBus } from '@/utils/EventBus'
import { LngLatLike, Map } from "maplibre-gl"
import { useStore } from '@/store/Store'
import { injectMap } from '@/utils/MapUtils'
import { TPoint } from '@/shared/types/geojson'
import { imageUrl } from '@/utils/ImageUrl'
import ArrowIcon from './icons/ArrowIcon.vue'

const props = defineProps<{
	features: TPoint[]
}>()

const $map = injectMap()
const store = useStore()

const featureDetails = useTemplateRef("featureDetails")

const featureId = ref<string | undefined>(undefined)
const anchor = ref<"left" | "right" | "bottom" | "">("")
const scrollable = ref(false)
const placementReady = ref(false)
let pendingPlacementRetryId: number | null = null
let panTimeoutId: ReturnType<typeof setTimeout> | null = null
let lockPopupAnchor = false
let flyLock = 0

type PopupAnchor = "left" | "right" | "bottom"
type ScreenPoint = { x: number, y: number }

const ARROW_PADDING = 8
const ARROW_INSET = 10
const DEFAULT_HALF_MARKER = 16

function markerHalfSize(placement?: MarkerPlacementSnapshot) {
	return {
		halfWidth: (placement ? (placement.maxX - placement.minX) / 2 : DEFAULT_HALF_MARKER) + ARROW_PADDING,
		halfHeight: (placement ? (placement.maxY - placement.minY) / 2 : DEFAULT_HALF_MARKER) + ARROW_PADDING,
	}
}

function placementCenter(placement: MarkerPlacementSnapshot): ScreenPoint {
	return {
		x: (placement.minX + placement.maxX) / 2,
		y: (placement.minY + placement.maxY) / 2,
	}
}

function popupTargetForAnchor(
	popupAnchor: PopupAnchor,
	centerPoint: ScreenPoint,
	popupWidth: number,
	popupHeight: number,
	halfMarkerWidth: number,
	halfMarkerHeight: number,
	mapWidth: number,
	clamp: boolean,
) {
	const targetPoint = { x: 0, y: 0 }
	let arrowDirection: "x" | "y" = "y"
	if (popupAnchor == "bottom") {
		arrowDirection = "x"
		targetPoint.x = centerPoint.x - popupWidth / 2
		targetPoint.y = centerPoint.y - popupHeight - halfMarkerHeight
		if (clamp) {
			targetPoint.x = Math.max(0, targetPoint.x)
			targetPoint.x = Math.min(mapWidth - popupWidth, targetPoint.x)
		}
	} else {
		arrowDirection = "y"
		targetPoint.y = centerPoint.y - popupHeight / 2
		if (clamp) {
			targetPoint.y = Math.max(0, targetPoint.y)
			targetPoint.y = Math.min(window.innerHeight - popupHeight, targetPoint.y)
		}
		if (popupAnchor == "right") {
			targetPoint.x = centerPoint.x - popupWidth - halfMarkerWidth
		} else {
			targetPoint.x = centerPoint.x + halfMarkerWidth
		}
	}

	let arrowOffset = centerPoint[arrowDirection] - targetPoint[arrowDirection]
	const popupSize = arrowDirection == "x" ? popupWidth : popupHeight
	if (clamp) {
		if (arrowOffset < ARROW_INSET) {
			targetPoint[arrowDirection] += (arrowOffset - ARROW_INSET)
			arrowOffset = ARROW_INSET
		} else if (arrowOffset > popupSize - ARROW_INSET) {
			targetPoint[arrowDirection] += (arrowOffset - popupSize + ARROW_INSET)
			arrowOffset = popupSize - ARROW_INSET
		}
	}

	return { targetPoint, arrowOffset }
}

function clearPendingPlacementRetry() {
	if (pendingPlacementRetryId != null) {
		window.cancelAnimationFrame(pendingPlacementRetryId)
		pendingPlacementRetryId = null
	}
}

function clearPanTimeout() {
	if (panTimeoutId != null) {
		clearTimeout(panTimeoutId)
		panTimeoutId = null
	}
}

function schedulePanToFit(diff: number) {
	clearPanTimeout()
	panTimeoutId = setTimeout(() => {
		panTimeoutId = null
		$map.panBy([diff, 0], { duration: 600 })
	}, 100)
}

function schedulePendingPlacementRetry() {
	if (pendingPlacementRetryId != null || !featureId.value) return
	pendingPlacementRetryId = window.requestAnimationFrame(() => {
		pendingPlacementRetryId = null
		update(false)
	})
}

function choosePopupAnchorAndOffset(
	centerPoint: ScreenPoint,
	popupWidth: number,
	popupHeight: number,
	halfMarkerWidth: number,
	halfMarkerHeight: number,
	mapWidth: number,
): { anchor: PopupAnchor, overflowX: number } {
	if (centerPoint.y - halfMarkerHeight - popupHeight > 0) {
		return { anchor: "bottom", overflowX: 0 }
	}
	if (centerPoint.x + halfMarkerWidth + popupWidth < mapWidth) {
		return { anchor: "left", overflowX: 0 }
	}
	if (centerPoint.x - halfMarkerWidth - popupWidth > 0) {
		return { anchor: "right", overflowX: 0 }
	}
	// Doesn't fit; keep left and shift the camera so the popup lands on-screen.
	// panBy([overflowX, 0]); flyTo offset is [-overflowX, 0].
	return {
		anchor: "left",
		overflowX: centerPoint.x + halfMarkerWidth + popupWidth - mapWidth,
	}
}

const feature = computed<TPoint | undefined>(() => {
	return props.features.find(f => f.id == featureId.value)
})

const categoryMarkers = computed<TPoint[]>(() => {
	const categoryId = feature.value?.properties?.__categoryId
	if (!categoryId) return []
	return props.features.filter(marker => marker.properties?.__categoryId === categoryId)
})

const categoryMarkerIndex = computed(() => {
	return categoryMarkers.value.findIndex(marker => marker.id == featureId.value)
})

const popupImageUrl = computed<string | undefined>(() => {
	if (!feature.value?.properties) return undefined
	return imageUrl(feature.value.properties.imageUrl, 270 * 2)
})

const googleMapsUrl = computed<string | undefined>(() => {
	const coordinates = feature.value?.geometry.coordinates
	if (!coordinates) return undefined
	const [longitude, latitude] = coordinates
	return `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`
})

const aspectRatio = computed<string>(() => {
	if (feature.value && feature.value.properties?.imageWidth && feature.value.properties?.imageHeight) {
		return `aspect-ratio: ${feature.value.properties?.imageWidth} / ${feature.value.properties?.imageHeight}; width: ${feature.value.properties?.imageWidth}px`
	}
	return ""
})

function stopLockedFly() {
	flyLock++
	lockPopupAnchor = false
	$map.off('moveend', onLockedFlyEnd)
}

function onLockedFlyEnd() {
	const lock = flyLock
	nextTick(() => {
		if (lock !== flyLock) return
		if ($map.isMoving()) return
		lockPopupAnchor = false
		$map.off('moveend', onLockedFlyEnd)
	})
}

function hasPopupContent(marker: TPoint | undefined) {
	if (!marker?.properties) return false
	if (marker.properties.imageUrl) return true
	if (store.localized(marker, 'label').trim()) return true
	return Boolean(store.localized(marker, 'description').replace(/<[^>]+>/g, ' ').trim())
}

function show({ id, fly }: { id: string | number | undefined, fly?: boolean }) {
	const marker = props.features.find(f => f.id == id)
	if (fly && marker) {
		if (!hasPopupContent(marker)) {
			hide()
			EventBus.emit("zoomTo", { features: [marker], speed: "fast" })
			return
		}
		showAndFlyToMarker(marker)
		return
	}
	if (!hasPopupContent(marker)) {
		hide()
		return
	}
	stopLockedFly()
	featureId.value = id as string
	placementReady.value = false
	clearPendingPlacementRetry()
	clearPanTimeout()
	nextTick(() => update(true))
}

function hide() {
	stopLockedFly()
	featureId.value = undefined
	placementReady.value = false
	clearPendingPlacementRetry()
	clearPanTimeout()
}

function flyToMarkerForPopup(marker: TPoint) {
	const el = featureDetails.value
	const mapWidth = $map.getContainer().offsetWidth
	const mapHeight = $map.getContainer().offsetHeight
	const popupWidth = el?.offsetWidth ?? 270
	const popupHeight = el?.offsetHeight ?? 120
	const placement = getLatestMarkerPlacement(String(marker.id))
	const { halfWidth, halfHeight } = markerHalfSize(placement)

	// Place the popup as if the marker is already at the fly destination (map center),
	// not at its current possibly off-screen position.
	const dest = { x: mapWidth / 2, y: mapHeight / 2 }
	if (placement) {
		const geo = $map.project(marker.geometry.coordinates as LngLatLike)
		const center = placementCenter(placement)
		dest.x += center.x - geo.x
		dest.y += center.y - geo.y
	}

	const plan = choosePopupAnchorAndOffset(dest, popupWidth, popupHeight, halfWidth, halfHeight, mapWidth)
	anchor.value = plan.anchor
	lockPopupAnchor = true

	const offset: [number, number] | undefined = plan.overflowX ? [-plan.overflowX, 0] : undefined
	EventBus.emit("zoomTo", { features: [marker], speed: "fast", offset })
	update(false)
	$map.off('moveend', onLockedFlyEnd)
	$map.on('moveend', onLockedFlyEnd)
}

function showAndFlyToMarker(marker: TPoint) {
	flyLock++
	lockPopupAnchor = true
	clearPanTimeout()
	$map.off('moveend', onLockedFlyEnd)
	$map.stop()
	featureId.value = marker.id as string
	placementReady.value = false
	clearPendingPlacementRetry()
	nextTick(() => flyToMarkerForPopup(marker))
}

function selectCategoryMarker(offset: number) {
	const markers = categoryMarkers.value
	const markerIndex = (categoryMarkerIndex.value + offset + markers.length) % markers.length
	const marker = markers[markerIndex]
	if (!marker) return
	EventBus.emit("showPopup", { id: marker.id, fly: true })
}

function handleKeydown(event: KeyboardEvent) {
	if (categoryMarkers.value.length <= 1) return
	if (event.key === "ArrowLeft") {
		event.preventDefault()
		event.stopPropagation()
		selectCategoryMarker(-1)
	} else if (event.key === "ArrowRight") {
		event.preventDefault()
		event.stopPropagation()
		selectCategoryMarker(1)
	}
}

function update(updateSide = false) {
	if (!feature.value) return

	const centerPoint = $map.project(feature.value.geometry.coordinates as LngLatLike)
	const placement = getLatestMarkerPlacement(String(feature.value.id))
	if (placement) {
		clearPendingPlacementRetry()
		const center = placementCenter(placement)
		centerPoint.x = center.x
		centerPoint.y = center.y
	} else if (lockPopupAnchor) {
		// Marker may still be off-screen; follow its projected point with the planned side.
		schedulePendingPlacementRetry()
	} else {
		placementReady.value = false
		schedulePendingPlacementRetry()
		return
	}
	if (!placementReady.value && !lockPopupAnchor) updateSide = true
	placementReady.value = true

	const el = featureDetails.value
	if (!el) return

	const popupWidth = el.offsetWidth
	const popupHeight = el.offsetHeight
	const mapWidth = $map.getContainer().offsetWidth
	const { halfWidth, halfHeight } = markerHalfSize(placement)

	scrollable.value = popupHeight >= window.innerHeight

	if (updateSide && !lockPopupAnchor) {
		const plan = choosePopupAnchorAndOffset(centerPoint, popupWidth, popupHeight, halfWidth, halfHeight, mapWidth)
		anchor.value = plan.anchor
		if (plan.overflowX) schedulePanToFit(plan.overflowX)
	}

	const popupAnchor: PopupAnchor = anchor.value == "right" || anchor.value == "bottom" ? anchor.value : "left"
	const { targetPoint, arrowOffset } = popupTargetForAnchor(
		popupAnchor,
		centerPoint,
		popupWidth,
		popupHeight,
		halfWidth,
		halfHeight,
		mapWidth,
		!lockPopupAnchor,
	)
	el.style?.setProperty('--arrow-top', `${arrowOffset}px`)
	el.style?.setProperty('transform', `translate(${targetPoint.x}px,${targetPoint.y}px)`)
}

function onMapMove() {
	update(false)
}

onMounted(() => {
	EventBus.on("showPopup", show)
	EventBus.on("updatePopup", update)
	EventBus.on("closePopup", hide)
	window.addEventListener("keydown", handleKeydown, true)

	$map?.on('move', onMapMove)
})

onBeforeUnmount(() => {
	clearPendingPlacementRetry()
	clearPanTimeout()
	stopLockedFly()
	EventBus.off("showPopup", show)
	EventBus.off("updatePopup", update)
	EventBus.off("closePopup", hide)
	window.removeEventListener("keydown", handleKeydown, true)
	$map?.off('move', onMapMove)
})
</script>


<template>
	<div ref="featureDetails" v-if="feature && feature.properties" class="feature-details"
		:class="[anchor, { pending: !placementReady }]">
		<div class="inner" :class="{ scrollable }">
			<img v-if="feature.properties.imageUrl" :key="feature.properties.imageUrl" :src="popupImageUrl"
				@load="update(false)" :style="aspectRatio" :class="{ fill: feature.properties.imageFill }">
			<h1>{{ store.localized(feature, 'label') }}</h1>
			<p v-html="store.localized(feature, 'description')"></p>
			<!-- <a v-if="googleMapsUrl" class="google-maps-link" :href="googleMapsUrl" target="_blank"
				rel="noopener noreferrer">Hitta hit</a> -->
			<div v-if="categoryMarkers.length > 1" class="category-navigation">
				<button type="button" aria-label="Previous marker" @click="selectCategoryMarker(-1)">
					<ArrowIcon direction="left" />
				</button>
				<span>{{ categoryMarkerIndex + 1 }} {{ store.uiText.of }} {{ categoryMarkers.length }}</span>
				<button type="button" aria-label="Next marker" @click="selectCategoryMarker(1)">
					<ArrowIcon direction="right" />
				</button>
			</div>
		</div>
	</div>
</template>



<style scoped>
.feature-details {
	position: absolute;
	top: 0;
	left: 0;
	max-width: 270px;

	max-height: 100%;
	display: flex;
	flex-flow: column nowrap;

	z-index: 100000000000;

	filter: drop-shadow(0 2px 4px #000000aa);
	--arrow-top: 50%;
}

.feature-details.pending {
	visibility: hidden;
	pointer-events: none;
}

@media (max-width: 550px) {
	.feature-details {
		max-width: 240px;
	}
}

.inner {
	background-color: #ffffff;
	padding: .5rem 1rem;
	border-radius: 6px;
	overflow: hidden;

	display: flex;
	flex-flow: column nowrap;
}

.inner.scrollable {
	overflow-y: scroll;
}

.google-maps-link {
	align-self: flex-start;
	margin-top: .5rem;
	color: #555;
	font-size: 12px;
	text-decoration: underline;
	text-underline-offset: 2px;
}

.google-maps-link:hover,
.google-maps-link:focus-visible {
	color: #111;
}

.category-navigation {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	margin-top: .5rem;
	padding-top: .175rem;
	border-top: solid 1px #f0f0f0;
	color: #999;
	font-size: 11px;
	font-variant-numeric: tabular-nums;
	margin-bottom: -.2rem;
}

.category-navigation button {
	display: grid;
	place-items: center;
	width: 24px;
	height: 24px;
	padding: 0;
	border: 0;
	border-radius: 50%;
	background: transparent;
	color: #777;
	cursor: pointer;
}

.category-navigation button:hover,
.category-navigation button:focus-visible {
	background: #f0f0f0;
}

.feature-details:after {
	z-index: -1;
	border: solid transparent;
	content: "";
	height: 0;
	width: 0;
	position: absolute;
	pointer-events: none;
	border-color: transparent;
	border-width: 8px;
}

.feature-details.left:after {
	top: var(--arrow-top);
	border-right-color: #ffffff;
	right: calc(100% - 1px);
	margin-top: -8px;
}

.feature-details.right:after {
	top: var(--arrow-top);
	border-left-color: #ffffff;
	left: calc(100% - 1px);
	margin-top: -8px;
}

.feature-details.bottom:after {
	border-top-color: #ffffff;
	top: calc(100% - 1px);
	left: var(--arrow-top);
	margin-left: -8px;

}


h1,
p {
	margin: 0;
}

h1 {
	font-weight: 800;
	font-size: .85rem;
	line-height: 1.2rem;
}

p {
	margin-top: .2rem;
	font-size: .75rem;
	line-height: 1.2rem;
	white-space: pre-wrap;
	word-break: break-word;
}

h1:empty,
p:empty {
	display: none;
}

img {
	max-width: 100%;
	margin-bottom: .4rem;
	object-fit: contain;
}

img.fill {
	max-width: calc(100% + 2rem);
	margin-top: -.5rem;
	margin-left: -1rem;
	margin-right: -1rem;

	border-top-left-radius: 6px;
	border-top-right-radius: 6px;
}
</style>